import { axios } from "utils";

import { PUBLIC_DIGITAL_AGRONOMIST_URL as endpoint } from "consts";

import {
  AddCropMixComposerCropsRequestBody,
  CropMixComposerCommercialMix,
  CropMixComposerCrop,
  CropMixComposerGoalResponse,
  CropMixComposerSettings,
  CropRecommendationParametersRequest,
  CropRecommendationResponse,
  CropRecommendationResponsePreference,
  MainCropOption,
  MutateCropMixComposerPreferencesRequest,
  ScoreFactors,
  UpdateDigitalAgronomistMainCropRequest,
} from "models";

import { FarmResilienceApiResponse } from "pages/app/resilience-score/utils/ResilienceScoreUtils";
import { ScenarioResilienceInput, ScenarioResilienceResult } from "pages/app/scenario-analysis/utils/scenarioResilienceScore";

export const getDigitalAgronomistCropsRecommendation = async ({ signal }: { signal: AbortSignal }): Promise<CropRecommendationResponse> =>
  axios.get(endpoint, { signal });

export const addOrUpdateDigitalAgronomistPreferences = async ({
  requestBody,
  signal,
}: {
  requestBody: CropRecommendationParametersRequest;
  signal: AbortSignal;
}): Promise<{ id: string }> => axios.post(`${endpoint}`, { data: requestBody, signal: signal });

export const getDigitalAgronomistMainCrops = async ({ signal }: { signal: AbortSignal }): Promise<MainCropOption[]> =>
  axios.get(`${endpoint}/main-crops`, { signal });

export const updateDigitalAgronomistPreferences = async ({
  id,
  requestBody,
}: {
  id: string;
  requestBody: CropRecommendationResponsePreference;
}): Promise<{ success: boolean }> => axios.put(`${endpoint}/${id}`, { data: requestBody });

export const updateDigitalAgronomistMainCrop = async ({
  preferenceId,
  year,
  requestBody,
}: UpdateDigitalAgronomistMainCropRequest): Promise<{ success: boolean }> => axios.put(`${endpoint}/main-crops/${preferenceId}/${year}`, { data: requestBody });

export const addDigitalAgronomistMainCrop = async ({
  preferenceId,
  year,
  requestBody,
}: UpdateDigitalAgronomistMainCropRequest): Promise<{ success: boolean }> =>
  axios.post(`${endpoint}/main-crops/${preferenceId}/${year}`, { data: requestBody });

export const deleteDigitalAgronomistMainCrop = async ({ preferenceId, year }: { preferenceId: string; year: number }): Promise<{ success: boolean }> =>
  axios.delete(`${endpoint}/main-crops/${preferenceId}/${year}`);

export const getCropMixComposerSettings = async ({ signal }: { signal: AbortSignal }): Promise<CropMixComposerSettings[]> =>
  axios.get(`${endpoint}/crop-mix-composer-settings`, { signal });

export const deleteCropMixComposerSettings = async ({ cropMixId }: { cropMixId: string }): Promise<{ success: boolean }> =>
  axios.delete(`${endpoint}/crop-mix-composer-settings/${cropMixId}`);

export const addOrUpdateCropMixComposerPreferences = async ({
  data,
  settingId,
}: {
  data: MutateCropMixComposerPreferencesRequest;
  settingId?: string;
}): Promise<{ success: boolean; id: string }> => {
  if (settingId) {
    return axios.put(`${endpoint}/crop-mix-composer-settings/${settingId}`, data);
  }

  return axios.post(`${endpoint}/crop-mix-composer-settings`, data);
};

export const getCropMixComposerCrops = async ({ cropMixId, signal }: { cropMixId: string; signal: AbortSignal }): Promise<CropMixComposerCrop[]> =>
  axios.get(`${endpoint}/crop-mix-composer-crops/${cropMixId}`, { signal });

export const addCropMixComposerCrops = async ({
  cropMixId,
  data,
}: {
  cropMixId: string;
  data: AddCropMixComposerCropsRequestBody;
}): Promise<CropMixComposerCrop[]> => axios.post(`${endpoint}/crop-mix-composer-crops/${cropMixId}`, data);

export const calculateCoverCropMixes = async ({ cropMixId }: { cropMixId: string }): Promise<CropMixComposerGoalResponse[]> =>
  axios.get(`${endpoint}/cover-crop-mixes/${cropMixId}`);

export const getCropMixComposerScoreFactors = async ({ signal }: { signal: AbortSignal }): Promise<ScoreFactors> =>
  axios.get(`${endpoint}/score-factors`, { signal });

export const calculateCropMixPotentialPayout = async ({
  fieldId,
  cropMixId,
  isComplexMix,
  signal,
}: {
  fieldId: string;
  cropMixId: string;
  isComplexMix: boolean;
  signal: AbortSignal;
}): Promise<{ potentialPayout: number }> => axios.post(`${endpoint}/crop-mix-composer-payout`, { data: { fieldId, cropMixId, isComplexMix }, signal });

export const getCropMixComposerCommercialMixes = async ({ mixId }: { mixId: string }): Promise<CropMixComposerCommercialMix[]> =>
  axios.get(`${endpoint}/${mixId}/commercial-mixes`);

export const getFarmResilienceScore = async ({ farmId, signal }: { farmId: string; signal: AbortSignal }): Promise<FarmResilienceApiResponse> =>
  axios.get(`${endpoint}/farms/${farmId}/resilience-score`, { signal });

export const getScenarioResilienceScore = async ({
  farmId,
  fieldId,
  projectionYears,
  scenarios,
}: {
  farmId: string;
  fieldId: string | null | undefined;
  projectionYears: number;
  scenarios: ScenarioResilienceInput[];
}): Promise<ScenarioResilienceResult[]> => axios.post(`${endpoint}/scenarios/resilience-score`, { farmId, fieldId, projectionYears, scenarios });
